import React, { useState, useEffect, useContext } from "react";
import { Row, Col, Button, Input, Label, Table, Spinner, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { AuthContext } from "../../../contexts/AuthContext";
import FotoRegistro from "./FotoRegistro";
import FotosArticulo from "../../Articulos/Articulos/FotosArticulo";
import DatosOrdenProduccion, { CAMPOS_DATOS_ORDEN } from "./DatosOrdenProduccion";
import ExplosionArticulo from "../../Ventas/Pedidos/ExplosionArticulo";

/**
 * Edita todo lo que sale en la Orden de Produccion (PDF) de una orden de maquila:
 * fechas, precio, consumo, observaciones, marca/tela/lavado/departamento, firmas,
 * el SKU de cada renglon, la foto del articulo y la habilitacion (explosion del
 * pedido, con foto de cada una). El maquilero se cambia en su propia ventana.
 *
 * Lee GET /cortes/:id/ficha?fotos=no y guarda con PATCH /cortes/:id/ordenProduccion.
 */
const URL_CORTES = process.env.REACT_APP_URL_CORTES;

const encabezados = () => ({
  headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
});

function EditarOrdenProduccion({ isOpen, toggle, idCorte, onGuardado, onVerPdf }) {
  const { user } = useContext(AuthContext);
  const [ficha, setFicha] = useState(null);
  const [form, setForm] = useState(null);
  const [skus, setSkus] = useState({});
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [modalExplosion, setModalExplosion] = useState(false);

  const cargar = () => {
    setCargando(true);
    return axios
      .get(`${URL_CORTES}/${idCorte}/ficha?fotos=no`, encabezados())
      .then(({ data }) => {
        setFicha(data);
        const e = data.editable || {};
        const catalogo = (data.articulo && data.articulo.catalogo) || {};
        const datos = {};
        // Lo vacio se prellena con el catalogo del articulo y con el usuario.
        CAMPOS_DATOS_ORDEN.forEach(({ campo }) => {
          datos[campo] = e[campo] || catalogo[campo] || "";
        });
        if (!datos.elaboradoPor) datos.elaboradoPor = [user && user.nombre, user && user.apellido].filter(Boolean).join(" ");
        setForm({
          fecha: e.fecha || "",
          fechaCompromiso: e.fechaCompromiso || "",
          precioMaquila: e.precioMaquila || 0,
          consumoRealPrenda: e.consumoRealPrenda || 0,
          observaciones: e.observaciones || "",
          datos,
        });
        setSkus(Object.fromEntries((data.piezas || []).map((p) => [p.articulosPedido, p.sku || ""])));
      })
      .catch((err) =>
        Swal.fire(
          "No se pudo cargar la orden",
          (err.response && err.response.data && err.response.data.message) || err.message,
          "error"
        )
      )
      .finally(() => setCargando(false));
  };

  useEffect(() => {
    if (!isOpen || !idCorte) return;
    setFicha(null);
    setForm(null);
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, idCorte]);

  const cambiar = (campo, valor) => setForm({ ...form, [campo]: valor });

  const guardar = async (verPdf) => {
    setGuardando(true);
    try {
      await axios.patch(
        `${URL_CORTES}/${idCorte}/ordenProduccion`,
        {
          fecha: form.fecha,
          fechaCompromiso: form.fechaCompromiso,
          precioMaquila: parseFloat(form.precioMaquila) || 0,
          consumoRealPrenda: parseFloat(form.consumoRealPrenda) || 0,
          observaciones: form.observaciones,
          ...form.datos,
          skus: Object.entries(skus).map(([articulosPedido, sku]) => ({ articulosPedido, sku: sku || "" })),
        },
        encabezados()
      );
      onGuardado && onGuardado();
      toggle();
      if (verPdf && onVerPdf) onVerPdf();
      else Swal.fire("Listo", "Orden de producción actualizada", "success");
    } catch (error) {
      const info = (error.response && error.response.data) || {};
      const detalle = info.validation && info.validation.body && info.validation.body.message;
      Swal.fire("No se pudo guardar", [info.message, detalle].filter(Boolean).join(": ") || error.message, "error");
    } finally {
      setGuardando(false);
    }
  };

  const art = (ficha && ficha.articulo) || null;
  const articuloExplosion = art ? { _id: art._id, codigo: art.codigo, descripcion: art.descripcion } : null;

  return (
    <>
      <Modal size="xl" isOpen={isOpen} toggle={toggle}>
        <ModalHeader toggle={toggle}>
          Orden de producción {ficha ? ficha.orden.numero : ""}
          {art ? ` · ${art.codigo || ""} ${art.descripcion || ""}` : ""}
        </ModalHeader>
        <ModalBody>
          {cargando || !form ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : (
            <>
              <Row>
                <Col md={2}>
                  <Label>Fecha</Label>
                  <Input type="date" value={form.fecha} onChange={(e) => cambiar("fecha", e.target.value)} />
                </Col>
                <Col md={2}>
                  <Label>Fecha compromiso</Label>
                  <Input type="date" value={form.fechaCompromiso} onChange={(e) => cambiar("fechaCompromiso", e.target.value)} />
                </Col>
                <Col md={2}>
                  <Label>Precio maquila x prenda</Label>
                  <Input type="number" value={form.precioMaquila} onChange={(e) => cambiar("precioMaquila", e.target.value)} />
                </Col>
                <Col md={2}>
                  <Label>Consumo tela x prenda</Label>
                  <Input
                    type="number"
                    value={form.consumoRealPrenda}
                    onChange={(e) => cambiar("consumoRealPrenda", e.target.value)}
                  />
                </Col>
                <Col md={4}>
                  <Label>Maquilero</Label>
                  <Input value={ficha.orden.maquilero} disabled title="Se cambia con el botón de editar maquilero" />
                </Col>
              </Row>
              <Row className="mt-2">
                <Col md={12}>
                  <Label>Observaciones</Label>
                  <Input value={form.observaciones} onChange={(e) => cambiar("observaciones", e.target.value)} />
                </Col>
              </Row>

              <h6 className="mt-3">Datos de la Orden de Producción</h6>
              <DatosOrdenProduccion valor={form.datos} onChange={(d) => cambiar("datos", d)} />

              <Row className="mt-2">
                <Col md={6}>
                  <h6>Piezas y SKU</h6>
                  <Table size="sm" bordered>
                    <thead>
                      <tr>
                        <th>Color</th>
                        <th>Talla</th>
                        <th className="text-right">A producir</th>
                        <th style={{ width: 170 }}>SKU</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(ficha.piezas || []).map((p) => (
                        <tr key={p.articulosPedido}>
                          <td>{p.color}</td>
                          <td>{p.talla}</td>
                          <td className="text-right">{p.cantidad}</td>
                          <td>
                            <Input
                              bsSize="sm"
                              placeholder="Código de barras"
                              value={skus[p.articulosPedido] || ""}
                              onChange={(e) =>
                                setSkus({ ...skus, [p.articulosPedido]: e.target.value.replace(/\D/g, "") })
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Col>
                <Col md={6}>
                  <h6>Fotos del artículo</h6>
                  {art ? <FotosArticulo id={art._id} tamano={80} /> : null}
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <h6 className="mb-0">
                      Habilitación{" "}
                      <small className="text-muted">
                        ({ficha.fuenteHabilitacion === "pedido" ? "explosión del pedido" : "receta del artículo"})
                      </small>
                    </h6>
                    <Button
                      size="sm"
                      color="info"
                      disabled={!ficha.pedido || !art}
                      onClick={() => setModalExplosion(true)}
                    >
                      <i className="fas fa-tape" /> Explosión
                    </Button>
                  </div>
                  <Table size="sm" bordered className="mt-2">
                    <tbody>
                      {(ficha.habilitacion || []).map((h) => (
                        <tr key={h._id}>
                          <td>
                            <div>{h.tipo}</div>
                            <small className="text-muted">
                              {[h.marca, h.color, h.proveedor].filter(Boolean).join(" · ")}
                            </small>
                          </td>
                          <td className="text-right" style={{ whiteSpace: "nowrap" }}>
                            {h.piezas} x prenda
                          </td>
                          <td style={{ width: 90 }}>
                            <FotoRegistro id={h._id} carpeta="habilitacion" tamano={40} compacto />
                          </td>
                        </tr>
                      ))}
                      {!(ficha.habilitacion || []).length ? (
                        <tr>
                          <td className="text-muted">Sin habilitación: captúrala con el botón Explosión.</td>
                        </tr>
                      ) : null}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={toggle} disabled={guardando}>
            Cancelar
          </Button>
          <Button color="danger" onClick={() => guardar(true)} disabled={guardando || !form}>
            <i className="far fa-file-pdf" /> Guardar y ver PDF
          </Button>
          <Button color="success" onClick={() => guardar(false)} disabled={guardando || !form}>
            {guardando ? <Spinner size="sm" /> : <i className="fas fa-save" />} Guardar
          </Button>
        </ModalFooter>
      </Modal>
      {ficha && ficha.pedido && articuloExplosion ? (
        <ExplosionArticulo
          isOpen={modalExplosion}
          toggle={() => setModalExplosion(false)}
          pedido={ficha.pedido._id}
          articulo={articuloExplosion}
          // Al guardar la explosion se relee la habilitacion de la orden, sin perder lo capturado.
          onListo={() =>
            axios
              .get(`${URL_CORTES}/${idCorte}/ficha?fotos=no`, encabezados())
              .then(({ data }) => setFicha({ ...ficha, habilitacion: data.habilitacion, fuenteHabilitacion: data.fuenteHabilitacion }))
              .catch(() => {})
          }
        />
      ) : null}
    </>
  );
}

export default EditarOrdenProduccion;
